import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table, TableRow, TableCell, Typography, Button, TableBody, Stack } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { DynamicIconButton } from '../../utils/tooltip/DynamicIconButton'
import { formatPercentage } from '../../packages/utils/numberFormatters'
import { calculateDiscount } from './utils'
import { IssueEntity } from '../../types/IssueEntity'

const Row = ({ label, shareDiscount, terpDiscount, includeTerpInTable }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    return (
        <TableRow>
            <TableCell sx={{ paddingLeft: '32px' }}>{label}</TableCell>
            <TableCell align='right'>
                {shareDiscount > 0 ? '+' : ''}
                {shareDiscount && shareDiscount !== 0 ? formatPercentage(shareDiscount, { locale, decimals: 0 }) : '-'}
            </TableCell>
            {includeTerpInTable ? (
                <TableCell align='right'>
                    {terpDiscount > 0 ? '+' : ''}
                    {terpDiscount && terpDiscount !== 0 ? formatPercentage(terpDiscount, { locale, decimals: 0 }) : '-'}
                </TableCell>
            ) : null}
        </TableRow>
    )
}

const getFormattedVwapSharePrice = (sharePrice, locale) => {
    if (isNaN(sharePrice)) return 'Ej tillgängligt'

    return (
        formatPercentage(sharePrice, {
            locale,
            decimals: 0,
            prefix: sharePrice > 0 ? '+' : '',
        }) + ' mot aktiekursen'
    )
}

function IssueTermsDiscountRow({ issue }: { issue: IssueEntity }) {
    const [extended, setExtended] = useState(false)
    const { i18n } = useTranslation()
    const locale = i18n.language

    if (issue.sharesInUnit === 0 && issue.type === 'Rights issue') {
        return null
    }

    if (!issue.unitSubPrice) return null

    const pub10VwapStockPrice =
        issue.tradesStats.find(({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10)
            ?.vwap || null
    const terms10VwapStockPrice =
        issue.tradesStats.find(({ periodType, numberOfDays }) => periodType === 'preTerms' && numberOfDays === 10)
            ?.vwap || null

    const default10VwapValue = issue.noTermsInitially || issue.termsDate ? terms10VwapStockPrice : pub10VwapStockPrice

    const default10VwapSharePrice = calculateDiscount(issue, default10VwapValue, false)
    const default10VwapTerp = calculateDiscount(issue, default10VwapValue, true)
    const formattedVwapSharePrice = getFormattedVwapSharePrice(default10VwapSharePrice, locale)

    const showDetailedTable = issue.tradesStats.some(
        (tradeStat) => ['prePublication', 'preTerms'].includes(tradeStat.periodType) && tradeStat.vwap !== null,
    )

    const discount10VwapExists = default10VwapSharePrice !== null || default10VwapTerp !== null

    const includeTerpInTable = issue.type === 'Rights issue'

    return (
        <TableRow
            sx={{
                '&:last-child th': {
                    border: 0,
                },
            }}
        >
            <TableCell
                sx={{
                    verticalAlign: 'top',
                    width: '50%',
                    paddingBottom: '16px',
                }}
            >
                <Stack direction='row' alignItems='center'>
                    <Typography>Rabatt {!extended && '(10d VWAP)'}</Typography>
                    {includeTerpInTable && (
                        <DynamicIconButton
                            title={
                                <>
                                    <p>Rabatten beräknas från tidpunkten då emissionen offentliggjordes.</p>
                                    <p>
                                        TERP-rabatt avser rabatten mot det teoretiska priset på aktien exklusive rätter.
                                    </p>
                                    <p>
                                        TERP = (Market cap + Antal nya aktier vid fullteckning * Teckningskurs) /
                                        (Maximalt antal aktier efter fulltecknad emission)
                                    </p>
                                </>
                            }
                            IconComponent={InfoIcon}
                            placement='top'
                            padding='0px 4px'
                        />
                    )}
                </Stack>

                {showDetailedTable ? (
                    <Button onClick={() => setExtended(!extended)} sx={{ padding: 0, justifyContent: 'left' }}>
                        {extended ? 'Dölj tabell' : 'Visa tabell'}
                    </Button>
                ) : null}
            </TableCell>
            {extended ? (
                <TableCell
                    colSpan={2}
                    sx={{
                        paddingRight: '0px',
                    }}
                >
                    <Table size='small' sx={{ maxWidth: '500px' }}>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <strong>Tidsperiod</strong>
                                </TableCell>
                                <TableCell align='right'>
                                    <strong>{includeTerpInTable ? 'Aktiekurs' : 'Rabatt'}</strong>
                                </TableCell>
                                {includeTerpInTable ? (
                                    <TableCell align='right'>
                                        <strong>TERP</strong>
                                    </TableCell>
                                ) : null}
                            </TableRow>
                            {issue.noTermsInitially || issue.termsDate ? (
                                <TableRow>
                                    <TableCell>
                                        <i>Från offentliggörande av emission</i>
                                    </TableCell>
                                    <TableCell colSpan={includeTerpInTable ? 2 : 1} />
                                </TableRow>
                            ) : null}
                            {issue.tradesStats
                                .filter(({ periodType }) => periodType === 'prePublication')
                                .map(({ vwap, numberOfDays }) => (
                                    <Row
                                        key={numberOfDays}
                                        label={`${numberOfDays} ${numberOfDays === 1 ? 'dag' : 'dagar'} VWAP`}
                                        shareDiscount={calculateDiscount(issue, vwap, false)}
                                        terpDiscount={calculateDiscount(issue, vwap, true)}
                                        includeTerpInTable={includeTerpInTable}
                                    />
                                ))}
                            {issue.noTermsInitially || issue.termsDate ? (
                                <>
                                    <TableRow>
                                        <TableCell>
                                            <i>Från det datum aktiekursen bestämdes</i>
                                        </TableCell>
                                        <TableCell colSpan={includeTerpInTable ? 2 : 1} />
                                    </TableRow>

                                    {issue.tradesStats
                                        .filter(({ periodType }) => periodType === 'preTerms')
                                        .map(({ vwap, numberOfDays }) => (
                                            <Row
                                                key={numberOfDays}
                                                label={`${numberOfDays} ${numberOfDays === 1 ? 'dag' : 'dagar'} VWAP`}
                                                shareDiscount={calculateDiscount(issue, vwap, false)}
                                                terpDiscount={calculateDiscount(issue, vwap, true)}
                                                includeTerpInTable={includeTerpInTable}
                                            />
                                        ))}
                                </>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableCell>
            ) : (
                <TableCell
                    sx={{
                        width: '50%',
                        paddingBottom: '16px',
                    }}
                >
                    {discount10VwapExists ? (
                        <>
                            <Typography>{formattedVwapSharePrice}</Typography>
                            {includeTerpInTable ? (
                                <Typography>
                                    {default10VwapTerp && default10VwapTerp > 0 ? '+' : ''}
                                    {formatPercentage(default10VwapTerp, {
                                        locale,
                                        decimals: 0,
                                    })}{' '}
                                    mot TERP
                                </Typography>
                            ) : null}
                        </>
                    ) : (
                        <Typography>Rabatter kan inte beräknas</Typography>
                    )}
                </TableCell>
            )}
        </TableRow>
    )
}

export default IssueTermsDiscountRow
